import type { Address, FieldDefinition, Money } from "@estelle/domain";
import type { ReactNode } from "react";

import { renderCell } from "@/components/table/cells";

/**
 * Money and dates on the customer page have to look exactly as they do in
 * every list — the same rupee grouping, the same IST day boundary. Handing a
 * minimal `FieldDefinition` to `renderCell` keeps one formatter in the
 * application rather than a second copy here that quietly drifts from the
 * first (the same approach as `modules/orders/format.tsx`, which this module
 * does not import so that neither depends on the other).
 */
const MONEY_FIELD: FieldDefinition = {
  name: "amount",
  label: "Amount",
  type: "money",
};
const DATE_FIELD: FieldDefinition = {
  name: "date",
  label: "Date",
  type: "date",
};
const DATETIME_FIELD: FieldDefinition = {
  name: "datetime",
  label: "Date",
  type: "datetime",
};

export function money(value: Money | null): ReactNode {
  return renderCell(MONEY_FIELD, value);
}

export function dateOnly(value: string | null): ReactNode {
  return renderCell(DATE_FIELD, value);
}

export function dateTime(value: string | null): ReactNode {
  return renderCell(DATETIME_FIELD, value);
}

/** `1 order`, `2 orders`. */
export function ordersLabel(count: number): string {
  return `${String(count)} order${count === 1 ? "" : "s"}`;
}

/** An address as lines, blank parts dropped rather than shown empty. */
export function addressLines(address: Address): string[] {
  const cityRow = [
    address.city,
    address.provinceCode ?? address.province,
    address.zip,
  ]
    .filter((part): part is string => Boolean(part && part.trim() !== ""))
    .join(", ");
  return [
    address.name,
    address.company,
    address.line1,
    address.line2,
    cityRow,
    address.country,
  ].filter((part): part is string => Boolean(part && part.trim() !== ""));
}
